import React, { useState, useEffect } from 'react';
import { Alert, Form, Input, Button, Card, message, Typography, Space, Modal } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { sendPasswordResetEmail } from 'firebase/auth';
import axios from 'axios';
import { auth } from '../firebase';
import { API_BASE_URL, getAuthHeaders } from '../api';
import { setDashboardPrefetchPromise } from '../prefetchCache';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [forgotVisible, setForgotVisible] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotForm] = Form.useForm();
  const [signInError, setSignInError] = useState<string | null>(null);
  const { login, refreshMe } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const onFinish = async (values: { email: string; password: string }) => {
    setSignInError(null);
    setLoading(true);
    try {
      const result = await login(values.email, values.password);
      if (result.success) {
        await refreshMe();
        message.success('Login successful!');
        // Start dashboard data fetch immediately so it's in flight when we navigate
        const headers = await getAuthHeaders();
        const prefetchPromise = Promise.all([
          axios.get(`${API_BASE_URL}/api/stats`, { headers }).then((r) => r.data).catch(() => null),
          axios
            .get(`${API_BASE_URL}/api/singapore/major-games-benchmarks`, { headers })
            .then((r) => r.data)
            .catch(() => null),
        ]).then(([stats, majorGamesBenchmarks]) => ({
          stats: stats ?? null,
          majorGamesBenchmarks: majorGamesBenchmarks ?? null,
        }));
        setDashboardPrefetchPromise(prefetchPromise);
        navigate('/dashboard');
      } else if (result.invalidCredentials) {
        const msg = 'Wrong email or password';
        setSignInError(msg);
        message.error(msg);
      } else {
        const msg = 'Login failed. Please try again.';
        setSignInError(msg);
        message.error(msg);
      }
    } catch (error) {
      const msg = 'Login failed. Please try again.';
      setSignInError(msg);
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const onForgotFinish = async (values: { email: string }) => {
    setForgotLoading(true);
    try {
      await sendPasswordResetEmail(auth, values.email);
      message.success('Check your email for a link to reset your password.');
      setForgotVisible(false);
      forgotForm.resetFields();
    } catch (error: any) {
      if (error?.code === 'auth/user-not-found') {
        message.error('No account found with this email.');
      } else if (error?.code === 'auth/invalid-email') {
        message.error('Please enter a valid email address.');
      } else {
        message.error('Could not send reset email. Try again later.');
      }
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div
      className="login-page"
      style={{
        padding: isMobile ? 16 : 24,
      }}
    >
      <Card
        className="login-card"
        style={{
          width: isMobile ? '100%' : 400,
          maxWidth: 400,
          padding: isMobile ? '20px 16px' : 24,
        }}
        styles={{ body: { padding: isMobile ? 16 : 24 } }}
      >
        <Space direction="vertical" size={isMobile ? 'middle' : 'large'} style={{ width: '100%', textAlign: 'center' }}>
          <div>
            <img
              src="/logo512.png"
              alt="TrackiAI logo"
              className="login-logo"
              style={{ width: isMobile ? 52 : 64, height: isMobile ? 52 : 64 }}
            />
            <Title level={isMobile ? 3 : 2} style={{ margin: 0, color: 'var(--text-primary)', fontSize: isMobile ? 20 : undefined }}>
              Tracki<span style={{ color: 'var(--racing-red)' }}>AI</span> Portal
            </Title>
            <Text className="login-subtitle" style={{ fontSize: isMobile ? 13 : undefined }}>
              Access the central athletics data repository
            </Text>
          </div>

          <Form
            name="login"
            onFinish={onFinish}
            autoComplete="off"
            size={isMobile ? 'middle' : 'large'}
          >
            {signInError ? (
              <Form.Item style={{ marginBottom: 16 }}>
                <Alert type="error" message={signInError} showIcon />
              </Form.Item>
            ) : null}
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please input your email!' },
                { type: 'email', message: 'Please enter a valid email!' }
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Email"
                autoComplete="email"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Please input your password!' }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Password"
                autoComplete="current-password"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 8 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                style={{ height: isMobile ? 44 : 48, fontSize: isMobile ? 15 : undefined }}
              >
                Sign In
              </Button>
            </Form.Item>
            <Form.Item style={{ marginBottom: 8, textAlign: 'center' }}>
              <Button type="link" onClick={() => setForgotVisible(true)} style={{ padding: 0, fontSize: isMobile ? 13 : 14 }}>
                Forgot password?
              </Button>
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, textAlign: 'center' }}>
              <Link
                to="/"
                className="login-back-to-home"
                style={{ fontSize: isMobile ? 13 : 14, color: 'var(--blue-bell)' }}
              >
                Back to home
              </Link>
            </Form.Item>
          </Form>
        </Space>
      </Card>

      <Modal
        title="Reset password"
        open={forgotVisible}
        onCancel={() => { setForgotVisible(false); forgotForm.resetFields(); }}
        footer={null}
        destroyOnHidden
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Enter your email and we&apos;ll send you a link to reset your password.
        </Text>
        <Form
          form={forgotForm}
          layout="vertical"
          onFinish={onForgotFinish}
          size={isMobile ? 'middle' : 'large'}
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input prefix={<UserOutlined />} placeholder="Email" autoComplete="email" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => { setForgotVisible(false); forgotForm.resetFields(); }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={forgotLoading}>
                Send reset link
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Login;
